import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { FEATURED_PROJECTS } from "../data/content";
import { useReveal } from "../hooks/useReveal";

const ProjectDetail = () => {
    const { id } = useParams();
    const ref = useReveal();
    const project =
        FEATURED_PROJECTS.find((p) => p.id === id) || FEATURED_PROJECTS[0];

    return (
        <div ref={ref} data-testid="project-detail-page" className="pt-24 md:pt-32">
            {/* Hero */}
            <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-12 md:py-20">
                <Link
                    to="/portfolio"
                    data-testid="project-back"
                    className="btn-ghost-gold mb-10"
                >
                    <ArrowLeft size={14} /> Back to Portfolio
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end reveal">
                    <div className="lg:col-span-8">
                        <p className="eyebrow mb-6">
                            {project.category} · {project.location} ·{" "}
                            {project.year}
                        </p>
                        <h1
                            className="font-display text-samora-ink leading-[0.95] tracking-tight"
                            style={{ fontSize: "clamp(2.5rem, 7vw, 6.5rem)" }}
                        >
                            {project.title}
                        </h1>
                    </div>
                    <p className="lg:col-span-4 text-samora-text/75 text-base md:text-lg leading-relaxed">
                        {project.summary}
                    </p>
                </div>
            </section>

            {/* Main image */}
            <section className="reveal">
                <img
                    src={project.cover}
                    alt={`${project.title} — cover`}
                    className="w-full h-[70vh] md:h-[90vh] object-cover"
                />
            </section>

            {/* Meta */}
            <section className="py-20 md:py-28 bg-samora-sand/40">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-12">
                    <div className="reveal">
                        <p className="eyebrow mb-3">Category</p>
                        <p className="font-display text-2xl text-samora-ink">
                            {project.category}
                        </p>
                    </div>
                    <div className="reveal">
                        <p className="eyebrow mb-3">Timeline</p>
                        <p className="font-display text-2xl text-samora-ink">
                            {project.timeline}
                        </p>
                    </div>
                    <div className="reveal">
                        <p className="eyebrow mb-3">Budget Range</p>
                        <p className="font-display text-2xl text-samora-ink">
                            {project.budget}
                        </p>
                    </div>
                    <div className="reveal">
                        <p className="eyebrow mb-3">Location</p>
                        <p className="font-display text-2xl text-samora-ink">
                            {project.location}
                        </p>
                    </div>
                </div>
            </section>

            {/* Materials + gallery */}
            <section className="py-24 md:py-32 bg-samora-bone">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-4 reveal">
                        <p className="eyebrow mb-6">Materials & Palette</p>
                        <ul className="space-y-3">
                            {project.materials.map((m) => (
                                <li
                                    key={m}
                                    data-testid={`material-${m}`}
                                    className="font-display text-2xl md:text-3xl text-samora-ink border-b border-samora-ink/15 pb-3"
                                >
                                    {m}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-8 space-y-6 md:space-y-8 reveal">
                        {project.gallery.map((g, i) => (
                            <img
                                key={i}
                                src={g}
                                alt={`${project.title} — detail ${i + 1}`}
                                className="w-full h-[60vh] md:h-[80vh] object-cover"
                                loading="lazy"
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-samora-ink text-samora-bone">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
                    <p className="eyebrow text-samora-gold mb-6">
                        Considering a home like this?
                    </p>
                    <h2
                        className="font-display leading-[0.95] tracking-tight max-w-3xl mx-auto"
                        style={{ fontSize: "clamp(2.25rem, 5vw, 5rem)" }}
                    >
                        Let's design yours.
                    </h2>
                    <Link
                        to="/contact"
                        data-testid="project-cta-contact"
                        className="btn-samora bg-samora-bone text-samora-ink border-samora-bone hover:bg-samora-gold hover:text-white hover:border-samora-gold mt-10"
                    >
                        Begin Your Project <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default ProjectDetail;
